import { useMemo, type ReactElement } from 'react';
import classNames from 'classnames';
import type { ObjectSchema } from 'yup';
import { FormProvider, type Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CTNInput from '@ctn/ui/components/ctn-input';
import CTNButton from '@ctn/ui/components/ctn-button';
import CTNDropdown from '@ctn/ui/components/ctn-dropdown';

import { defaultValues, formSchema, type HemerotecaSearchValues } from './config';
import { buildTomoOptions, buildYearOptions } from './options';

type HemerotecaSearchFormProps = {
	readonly className?: string;
	readonly onSubmitSuccess?: (payload: HemerotecaSearchValues) => void;
};

const classNameComponent = 'c-cnm-hemeroteca-form';

export const HemerotecaSearchForm = ({ className, onSubmitSuccess }: HemerotecaSearchFormProps): ReactElement => {
	const wrapperClassName = classNames(classNameComponent, className);

	const resolver = useMemo<Resolver<HemerotecaSearchValues>>(
		() => yupResolver<HemerotecaSearchValues>(formSchema as unknown as ObjectSchema<HemerotecaSearchValues>),
		[],
	);

	const methods = useForm<HemerotecaSearchValues>({
		mode: 'onTouched',
		resolver,
		defaultValues,
	});

	const yearOptions = useMemo(() => buildYearOptions(100), []);
	const tomoOptions = useMemo(() => buildTomoOptions(64), []);

	const {
		control,
		formState: { errors, isSubmitting },
		handleSubmit,
	} = methods;

	const submit = handleSubmit((data) => onSubmitSuccess?.({ ...data }));

	return (
		<div className={wrapperClassName}>
			<FormProvider {...methods}>
				<form noValidate className="[ o-grid ]" onSubmit={(e) => e.preventDefault()}>
					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="autor"
								label="Autor"
								classNameField={`${classNameComponent}__field`}
								classNameInput={`${classNameComponent}__input`}
								error={errors.autor?.message}
							/>
						</div>
					</div>
					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="titulo"
								label="Título"
								classNameField={`${classNameComponent}__field`}
								classNameInput={`${classNameComponent}__input`}
								error={errors.titulo?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12 o-grid__col-6@desktop">
							<CTNDropdown
								control={control}
								name="year"
								label="Año"
								options={yearOptions}
								placeholder="Selecciona año"
								classNameField={`${classNameComponent}__field`}
								error={errors.year?.message}
							/>
						</div>

						<div className="o-grid__col-12 o-grid__col-6@desktop">
							<CTNDropdown
								control={control}
								name="tomo"
								label="Tomo"
								options={tomoOptions}
								placeholder="Selecciona tomo"
								classNameField={`${classNameComponent}__field`}
								error={errors.tomo?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12 o-grid__col-6@landscape o-grid__col-3@desktop">
							<CTNButton
								type="button"
								variant="solid"
								mode="primary"
								size="lg"
								className={`${classNameComponent}__btn`}
								label={isSubmitting ? 'Buscando...' : 'Buscar'}
								disabled={isSubmitting}
								onClick={submit}
							/>
						</div>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
